/**
 * Mitigation-to-Highlight Resolver — Theme A (Bill Kinney walkthrough 2026-05-14).
 *
 * Given a medchem rule's `mitigationOptions` entry + a target compound's
 * SMILES, returns the atom indices on the compound that the mitigation
 * affects, so that the rendering layer (`MitigationVisual`) can highlight
 * those atoms on the 2D structure.
 *
 * Each (ruleId, strategy) pair has a `match SMARTS` (used to locate the
 * relevant substructure) and an `atomPicker` that selects the subset of
 * matched atoms to highlight. For aniline ortho-block, the SMARTS finds
 * the aniline ring and the picker returns the two ortho carbons.
 *
 * **v1 chemistry coverage**: the four `mitigationOptions` strategies on
 * the `tox_aniline_dili` rule. Other rules adopt the same descriptor
 * pattern as their `mitigationOptions` populate.
 *
 * @module gibbs/knowledge/mitigationHighlightResolver
 */

import { loadRDKit } from './rdkitLoader';

// ─── Per-(rule, strategy) descriptors ──────────────────────────────────────
//
// Each descriptor:
//   - `matchSmarts` — SMARTS pattern run against the compound to locate
//     the substructure the strategy targets. The match-atom ordering
//     follows the SMARTS atom order; the picker indexes into it.
//   - `pickIndices` — array of indices INTO the SMARTS match that should
//     be highlighted (NOT into the compound; the resolver translates).
//   - `caption` — short prose hint for the screen-reader alt-text and
//     the visual's caption strip.
//   - `colour` — RGB triple for the highlight (kept here so adjacent
//     strategies don't all look identical when shown side-by-side).
//
// Example for aniline ortho-block:
//   matchSmarts: '[NX3;H2][c]1[cH][cH][cH][cH][cH]1'
//                 ^0       ^1  ^2  ^3  ^4  ^5  ^6
//   pickIndices: [2, 6]  → the two ortho ring carbons

const HIGHLIGHT_DESCRIPTORS = {
    tox_aniline_dili: {
        'ortho-block': {
            matchSmarts: '[NX3;H2][c]1[cH][cH][cH][cH][cH]1',
            pickIndices: [2, 6],
            caption: 'ortho positions (add steric block here)',
            colour: [0.95, 0.2, 0.2],
        },
        'ewg-para': {
            matchSmarts: '[NX3;H2][c]1[cH][cH][cH][cH][cH]1',
            pickIndices: [4],
            caption: 'para position (add EWG here, e.g. -CN, -CF3)',
            colour: [0.2, 0.4, 0.95],
        },
        'N-to-C-swap': {
            // Two SMARTS to try — piperazine first, then piperidine. The
            // resolver tries each in order and uses the first that matches.
            // Piperazine has two nitrogens; we highlight both. Piperidine
            // has one.
            matchSmartsList: [
                { smarts: 'N1CCNCC1', pickIndices: [0, 3] },
                { smarts: 'N1CCCCC1', pickIndices: [0] },
            ],
            caption: 'piperazine/piperidine N → replace with C',
            colour: [0.6, 0.3, 0.85],
        },
        'amide-or-sulfonamide': {
            matchSmarts: '[NX3;H2][c]',
            pickIndices: [0],
            caption: 'aniline N → convert to amide/sulfonamide',
            colour: [0.2, 0.75, 0.4],
        },
    },
};

/**
 * Resolve a mitigation strategy to a set of atom indices on the target
 * compound.
 *
 * @param {string} ruleId - The medchem rule id (e.g., 'tox_aniline_dili')
 * @param {string} strategy - The mitigationOption.strategy field
 * @param {string} smiles - Target compound SMILES
 * @returns {Promise<{
 *   atomIndices: number[],
 *   caption: string,
 *   colour: number[],
 * } | null>}
 *   atomIndices is empty when the strategy's SMARTS doesn't match the
 *   compound (caller should fall back to prose-only). Returns null when
 *   the (ruleId, strategy) pair has no descriptor or RDKit is unavailable.
 */
export async function resolveMitigationHighlight(ruleId, strategy, smiles) {
    if (!ruleId || !strategy || !smiles) return null;

    const ruleDescriptors = HIGHLIGHT_DESCRIPTORS[ruleId];
    if (!ruleDescriptors) return null;
    const descriptor = ruleDescriptors[strategy];
    if (!descriptor) return null;

    let RDKit;
    try {
        RDKit = await loadRDKit();
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
            '[mitigationHighlightResolver] RDKit unavailable:', err?.message
        );
        return null;
    }
    if (!RDKit.isInitialized || !RDKit.isInitialized()) return null;

    let mol = null;
    try {
        mol = RDKit.getMol(smiles);
        if (!mol || !mol.is_valid()) return null;
    } catch (err) {
        if (mol && mol.delete) mol.delete();
        return null;
    }

    // Multi-SMARTS descriptors (e.g., piperazine OR piperidine) iterate
    // until one matches; single-SMARTS descriptors run once.
    const attempts = Array.isArray(descriptor.matchSmartsList)
        ? descriptor.matchSmartsList
        : [{ smarts: descriptor.matchSmarts, pickIndices: descriptor.pickIndices }];

    const atomIndices = [];
    try {
        for (const attempt of attempts) {
            const matchedAtoms = runSubstructMatch(RDKit, mol, attempt.smarts);
            if (matchedAtoms.length === 0) continue;
            for (const pick of attempt.pickIndices) {
                if (pick >= 0 && pick < matchedAtoms.length) {
                    atomIndices.push(matchedAtoms[pick]);
                }
            }
            // First-match-wins for multi-SMARTS descriptors
            if (atomIndices.length > 0) break;
        }
    } finally {
        if (mol && mol.delete) mol.delete();
    }

    return {
        atomIndices,
        caption: descriptor.caption,
        colour: descriptor.colour,
    };
}

function runSubstructMatch(RDKit, mol, smarts) {
    let qmol = null;
    try {
        qmol = RDKit.getQMol(smarts);
        const matchStr = mol.get_substruct_match(qmol);
        if (!matchStr || matchStr === '{}') return [];
        const parsed = JSON.parse(matchStr);
        return Array.isArray(parsed.atoms) ? parsed.atoms : [];
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
            `[mitigationHighlightResolver] SMARTS match failed for "${smarts}":`,
            err?.message
        );
        return [];
    } finally {
        if (qmol && qmol.delete) qmol.delete();
    }
}

/**
 * For tests + introspection: which (ruleId, strategy) pairs have descriptors.
 *
 * @returns {Array<{ ruleId: string, strategy: string }>}
 */
export function listSupportedMitigationVisuals() {
    const out = [];
    for (const ruleId of Object.keys(HIGHLIGHT_DESCRIPTORS)) {
        for (const strategy of Object.keys(HIGHLIGHT_DESCRIPTORS[ruleId])) {
            out.push({ ruleId, strategy });
        }
    }
    return out;
}
